import { MultiEventsFeed } from "../../../../eventer-pod/client/events-feed/multi-events-feed.ts";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { EventCard } from "./EventCard.tsx";
import EmptyEventsFeedPage from "./EmptyEventsFeedPage.tsx";
import { useBrandingTitleSuffix } from "../../../../appfw/ui/branding/BrandingControl.tsx";

import FullSizeBox from "../../../../appfw/ui/components/FullSizeBox.tsx";
import { useW2d2dPod } from "../../hooks/useW2d2dPod";
import { useLoadingControl } from "../../../../appfw/loading/use-loading-control.tsx";
import { useLocation } from "react-router-dom";
import { scrollToElementWhenStable } from "../../../../appfw/ui/utils/scroll-to-element.ts";
import LoadingScreen from "../../../../appfw/ui/loading/loading-screen.tsx";
import { usePodsControl } from "../../hooks/use-pods-control.tsx";
import { MultiFeedEntry } from "../../../../eventer-pod/client/events-feed/events-feed-utils.ts";

export default function EventsFeedPage() {

    const pod = useW2d2dPod()
    const { activePods } = usePodsControl()
    const location = useLocation()
    const [eventsFeed, setEventsFeed] = useState<undefined | MultiEventsFeed>()
    const [entries, setEntries] = useState<undefined | MultiFeedEntry[]>()
    const loadingControl = useLoadingControl("events-feed")
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const shouldScrollToEvent = useRef<string | null>(null)

    const handleScroll = useCallback(async (event: React.UIEvent<HTMLDivElement>) => {

        if (!eventsFeed || loadingControl.isLoadingId("loadNext")) {
            return
        }

        const container = event.currentTarget
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        // const nearTop = scrollTop < 200
        // if (!firstEventsRendering && nearTop && eventsFeed.eventsFeed.hasPrev()) {
        //     console.log("scroll: 🔁 Near top → loading prev")
        //
        //     const prevScrollHeight = container.scrollHeight;
        //     eventsFeed.eventsFeed.loadPrev().then(() => {
        //         requestAnimationFrame(() => {
        //             const newScrollHeight = container.scrollHeight;
        //             const diff = newScrollHeight - prevScrollHeight;
        //             container.scrollTop = scrollTop + diff;
        //
        //             console.log("scroll: 🔁 scrollTop corrected by", diff);
        //         })
        //     })
        // }

        const nearBottom = scrollTop + clientHeight >= scrollHeight - 200;
        if (nearBottom && eventsFeed.hasNext()) {
            console.log("scroll: 🔁 Near bottom → loading next");
            await loadingControl.withLoading(() => eventsFeed.loadNext(), "loadNext")
        }
    }, [eventsFeed, loadingControl])

    const scrollToEvent = useCallback((eventUri: string) => {
        scrollToElementWhenStable(`[data-anchor-id="${eventUri}"]`, {
            container: scrollContainerRef.current,
            block: 'start',
            debug: true
        }).then(success => {
            if (!success) {
                console.warn("Event element not found, cannot scroll to:", eventUri)
            }
        })
    }, [])

    useBrandingTitleSuffix("Veranstaltungen")

    useEffect(() => {
        if (!pod || !activePods) {
            return
        }

        loadingControl.withLoading(async () => {

            const onEntriesChanged: (entries: MultiFeedEntry[]) => void = (entries) => {
                console.log("set events called with", entries.length, "events")
                setEntries(entries)
            }

            const podNames = activePods.map(p => p.name)

            const eventsFeedControl = new MultiEventsFeed(
                pod,
                // explicit only public events
                null,
                podNames,
                onEntriesChanged,
            )
            setEventsFeed(eventsFeedControl)

            await eventsFeedControl.initNext()

        }, "init")

    }, [pod, activePods])

    // Check if we should scroll to a specific event (coming back from detail page)
    useEffect(() => {
        if (location.state?.eventUri) {
            shouldScrollToEvent.current = location.state.eventUri
        }
    }, [location.state])

    // Trigger scroll after events are rendered
    useEffect(() => {
        // Only scroll if:
        // 1. We have an event to scroll to
        // 2. Events are loaded
        // 3. EventsFeed is initialized
        // 4. Not currently loading
        if (
            shouldScrollToEvent.current &&
            entries &&
            entries.length > 0 &&
            eventsFeed &&
            !loadingControl.isLoadingId("init")
        ) {
            const scrollTo = shouldScrollToEvent.current
            shouldScrollToEvent.current = null

            // Double requestAnimationFrame to ensure DOM is fully rendered
            requestAnimationFrame(() => {
                // requestAnimationFrame(() => {
                scrollToEvent(scrollTo)
                // })
            })
        }
    }, [entries, eventsFeed, loadingControl, scrollToEvent])

    if (entries === undefined || eventsFeed === undefined || loadingControl.isLoadingId("init")) {
        return <LoadingScreen message="Lade Veranstaltungen" />
    }

    if (entries.length === 0) {
        return <EmptyEventsFeedPage />
    }

    return (
        <FullSizeBox>

            <Box
                ref={scrollContainerRef}
                onScroll={handleScroll}
                sx={{
                    flex: 1,                        // nimmt Resthöhe ein
                    overflowY: 'auto',              // wirklich scrollbar
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {entries.map((entry) => (
                    <EventCard
                        pod={eventsFeed.localEventerPod}
                        event={entry.event}
                        remotePod={entry.remotePod}
                        key={`${entry.remotePod}::${entry.event.subject}`}
                    />
                ))}
            </Box>

        </FullSizeBox>
    )
}